// <HeroCard> — Home View welcome card with three CTAs (T-259).
//
// Renders the welcome hero at the top of the Home view:
//   - 80×80 circular avatar with white/30 border + shadow-xl
//   - "Welcome back" eyebrow (uppercase tracking-widest)
//   - greetingName (server-resolved — "Lene" or "there" fallback)
//   - Subline now shows the viewer's Employee.title when known
//     (e.g. "Head of Talent and Culture"); the date copy lives one
//     line up if it's still useful, otherwise we drop it.
//   - Three CTAs side-by-side:
//       1. "Go to profile" — Link to /employees/:id (primary white-fill)
//       2. "Request time off" — opens <RequestModal> (transparent white)
//       3. <ClockInButton> — toggles clock state (transparent white)
//
// All three CTAs gate on `hero.myProfileEmployeeId`: when null
// (bootstrap users), each button renders disabled with a tooltip
// pointing at the People view.
//
// The server still emits `hero.primaryCta` for backwards-compat but
// the three buttons are now always shown — the field is no longer
// consulted client-side.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HomeHero, TimeOffTabResponse } from '@pa/shared';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../api/client';
import { RequestModal } from '../profile/tabs/timeOff/RequestModal';
import { ClockInButton } from './ClockInButton';

export interface HeroCardProps {
  hero: HomeHero;
}

export function HeroCard({ hero }: HeroCardProps) {
  const { me, refresh } = useAuth();
  const queryClient = useQueryClient();
  const avatarUrl = me?.user.avatarUrl;
  const initial = (hero.greetingName ?? '?').slice(0, 1).toUpperCase();
  const empId = hero.myProfileEmployeeId;
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  // T-262 — self-heal mutation. When an OWNER / ADMIN has no
  // linked Employee (their org was bootstrapped before the
  // `autoCreateOwnerEmployee` default flipped on), the home hero
  // swaps the three disabled CTAs for a single "Set up your
  // profile" button that POSTs `/api/me/ensure-employee` and then
  // refreshes the home + me queries. Once the new Employee lands,
  // the buttons unlock on the next render.
  const isOwnerOrAdmin =
    me?.user.role === 'OWNER' || me?.user.role === 'ADMIN';
  const canSelfHeal = !empId && isOwnerOrAdmin;
  const ensureEmployeeMutation = useMutation({
    mutationFn: () =>
      api.post<{ ok: true; action: string; employeeId: string }>(
        '/api/me/ensure-employee',
        {},
      ),
    onSuccess: async () => {
      // Repull the home payload (myProfileEmployeeId flips
      // non-null) + the /me query (so the sidebar profile chip,
      // and downstream queries that key off employeeId, light
      // up). `refresh()` from AuthContext is the canonical /me
      // reload.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['home'] }),
        refresh(),
      ]);
    },
  });

  // Lazy-fetch the time-off tab payload so the modal has its
  // `policies` ready when opened. The same query key is shared with
  // the Profile/Time-off tab so an open modal on one surface
  // invalidates the other automatically after submit.
  //
  // We DON'T pre-fetch on mount — the modal is rare; only kick the
  // query when the user clicks "Request time off". `enabled` flips
  // to `true` on open.
  const policiesQuery = useQuery({
    queryKey: ['timeoff-policies', empId],
    queryFn: () =>
      api.get<TimeOffTabResponse>(
        `/api/employees/${empId}/timeoff/tab?month=${formatYearMonth(new Date())}`,
      ),
    enabled: requestModalOpen && !!empId,
  });

  return (
    <section
      data-testid="home-hero"
      className="relative rounded-2xl overflow-hidden shadow-lg"
      style={{
        background:
          'linear-gradient(135deg, #6344C6 0%, #8B6DE8 50%, #A78BFA 100%)',
      }}
    >
      {/* Decorative circles per spec §1 / §4.1 */}
      <span
        aria-hidden
        className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-white/5 pointer-events-none"
      />
      <span
        aria-hidden
        className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-white/5 pointer-events-none"
      />
      <div className="relative z-10 px-8 py-8 flex flex-col items-center text-center gap-4">
        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white/30 shadow-xl flex-shrink-0 bg-white/10 flex items-center justify-center">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <span
              className="text-white text-2xl font-bold"
              data-testid="home-hero-avatar-fallback"
            >
              {initial}
            </span>
          )}
        </div>
        <div>
          <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-1">
            Welcome back
          </p>
          <h2 className="text-white text-2xl font-bold leading-tight">
            {hero.greetingName}
          </h2>
          {hero.subline && (
            <p className="text-white/70 text-sm mt-0.5">{hero.subline}</p>
          )}
        </div>
        {canSelfHeal ? (
          // T-262 — Self-heal banner: replaces the disabled 3-CTA
          // row when an OWNER/ADMIN has no Employee link. One
          // click creates the row + reloads the home payload, then
          // the normal CTAs render on the next pass.
          <div className="flex flex-col items-center gap-2 mt-1">
            <p className="text-white/80 text-sm max-w-md">
              You're not on the People roster yet — that's blocking your time-off
              and clock-in features. One click sets it up.
            </p>
            <button
              type="button"
              onClick={() => ensureEmployeeMutation.mutate()}
              disabled={ensureEmployeeMutation.isPending}
              data-testid="home-hero-self-heal-cta"
              className="bg-white text-[#6344C6] px-5 py-2 rounded-xl text-sm font-bold hover:bg-white/90 shadow-md hover:shadow-lg active:scale-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {ensureEmployeeMutation.isPending
                ? 'Setting up…'
                : 'Set up my profile'}
            </button>
            {ensureEmployeeMutation.isError && (
              <p
                className="text-rose-200 text-xs"
                data-testid="home-hero-self-heal-error"
              >
                Couldn't set up the profile — try again, or add yourself manually
                from the People view.
              </p>
            )}
          </div>
        ) : (
        <div className="flex flex-wrap justify-center gap-3 mt-1">
          {/* CTA 1 — Go to profile (primary, white fill) */}
          {empId ? (
            <Link
              to={`/employees/${empId}`}
              data-testid="home-hero-primary-cta"
              data-cta="go_to_profile"
              className="bg-white text-[#6344C6] px-5 py-2 rounded-xl text-sm font-bold hover:bg-white/90 shadow-md hover:shadow-lg active:scale-95 transition"
            >
              Go to profile
            </Link>
          ) : (
            <button
              type="button"
              disabled
              data-testid="home-hero-primary-cta"
              data-cta="go_to_profile"
              className="bg-white/40 text-white/80 px-5 py-2 rounded-xl text-sm font-bold cursor-not-allowed"
              title="Add yourself to the roster from the People view to enable this action."
            >
              Go to profile
            </button>
          )}

          {/* CTA 2 — Request time off (opens the modal) */}
          <button
            type="button"
            onClick={() => setRequestModalOpen(true)}
            disabled={!empId}
            data-testid="home-hero-request-time-off-cta"
            data-cta="request_time_off"
            className={
              empId
                ? 'bg-white/15 hover:bg-white/25 text-white px-5 py-2 rounded-xl text-sm font-bold border border-white/30 transition active:scale-95'
                : 'bg-white/10 text-white/60 px-5 py-2 rounded-xl text-sm font-bold border border-white/20 cursor-not-allowed'
            }
            title={
              empId
                ? undefined
                : 'Add yourself to the roster from the People view to enable this action.'
            }
          >
            Request time off
          </button>

          {/* CTA 3 — Clock in / out (own state + mutation) */}
          <ClockInButton employeeId={empId ?? null} />
        </div>
        )}
      </div>

      {/* Request Time Off modal — only mounts when triggered, only
          shows the form once policies have arrived. */}
      {requestModalOpen && empId && (
        <>
          {policiesQuery.isLoading && (
            <div
              className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50"
              data-testid="home-hero-request-modal-loading"
            >
              <div className="bg-white rounded-2xl shadow-xl p-6 text-slate-500">
                Loading policies…
              </div>
            </div>
          )}
          {policiesQuery.data && (
            <RequestModal
              employeeId={empId}
              policies={policiesQuery.data.policies}
              onClose={() => setRequestModalOpen(false)}
            />
          )}
        </>
      )}
    </section>
  );
}

function formatYearMonth(d: Date): string {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}`;
}
